import isEqual from 'lodash.isequal';
import React, { ComponentType, PureComponent } from 'react';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { asap } from 'rxjs/scheduler/asap';

const DEFAULT_POLL_TIME = 2 * 60 * 1000;

/**
 * Fires one event directly, suspends and resumes on document.resume and document.pause events
 * @param time interval in milliseconds
 */
export function Poller$(time: number = DEFAULT_POLL_TIME): Observable<any> {
  return Observable.fromEvent(document, 'resume') // fires when we resume
    .startWith(1) // always fires once
    .switchMap(() => {
      // on resume and the first time we start the poller,
      return Observable.timer(time, asap) // a poller
        .startWith(1)
        .takeUntil(Observable.fromEvent(document, 'pause')); // stops when we pause
    });
}

export function mapObservable<
  TOuter extends {},
  T extends {},
  TInnner extends {},
>(
  filterer: (s: TOuter) => T,
  mapper: (t: T) => Observable<TInnner>,
  init: TInnner = null as TInnner,
) {
  return (ComposedComponent: ComponentType<TInnner>) =>
    class MapObservableInternal extends PureComponent<TOuter, TInnner> {
      state = init;
      private subscription: Subscription;
      private props$ = new BehaviorSubject(this.props);
      private buffer = new BehaviorSubject(init);

      componentWillMount() {
        const input$ = this.props$.map(filterer).distinctUntilChanged(isEqual);

        input$
          .switchMap((t) => mapper(t).takeUntil(input$.skip(1)))
          .distinctUntilChanged(isEqual)
          .catch((e, o) => {
            console.error(`error ${e} in component ${ComposedComponent}`, {
              e,
              o,
            });
            return Observable.of(null);
          })
          .subscribe(this.buffer);

        if (this.buffer.getValue()) {
          this.setState(this.buffer.getValue());
        }
      }

      componentDidMount() {
        this.subscription = this.buffer.subscribe((v) =>
          v ? this.setState(v) : null,
        );
      }

      componentWillReceiveProps(p) {
        this.props$.next(p);
      }

      componentWillUnmount() {
        this.subscription.unsubscribe();
      }

      render() {
        if (!this.state) {
          return null;
        }
        return <ComposedComponent {...this.props} {...this.state} />;
      }
    };
}

export type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
export type Subtract<T, K> = Omit<T, keyof K>;

export function addObservable<TInjected>(
  observable: Observable<TInjected>,
  init: TInjected = null,
) {
  const buffer = new BehaviorSubject(init);
  observable.distinctUntilChanged(isEqual).subscribe(buffer);

  return function <P>(
    ComposedComponent: ComponentType<P & TInjected>,
  ): ComponentType<Subtract<P, TInjected>> {
    return class AddObservableInternal extends PureComponent<P, TInjected> {
      private subscription: Subscription;

      state: TInjected = init;

      componentWillMount() {
        // container either the init or a latest value from an existing stream;
        if (buffer.getValue()) {
          this.setState(buffer.getValue());
        }
      }

      componentDidMount() {
        this.subscription = buffer
          .catch((e, o) => {
            console.error(`error ${e} in component ${ComposedComponent}`, {
              e,
              o,
            });
            return Observable.of(null);
          })
          .subscribe((v) => (v ? this.setState(v) : null));
      }

      componentWillUnmount() {
        if (this.subscription) {
          this.subscription.unsubscribe();
        }
      }

      render() {
        if (!this.state) {
          return null;
        }
        return <ComposedComponent {...this.props} {...this.state} />;
      }
    };
  };
}
